import React, { useEffect, useRef, type ReactElement } from 'react';
import type { UIFieldDefinition } from '../../utils/prepareSendIssueFields';
import { Select, TextField } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';

export interface ProvideValuesComponentProps {
  uiField: UIFieldDefinition;
  isRequired: boolean;
  setUIField: (updatedUIField: UIFieldDefinition) => void;
}

export const ProvideValuesComponent = ({
  uiField,
  isRequired,
  setUIField
}: ProvideValuesComponentProps): ReactElement => {
  const { t } = useTranslation();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  let error: string | undefined = undefined;
  if (uiField.error) {
    if (uiField.error.isInvalidValue) {
      error = t('Invalid value');
    }

    if (uiField.error.isLengthTooLong) {
      error = t('Value is too long');
    }

    if (uiField.error.isMissingRequired) {
      error = t('Value is required');
    }
  }

  const isSelect =
    uiField.uiFieldType === 'select' || uiField.uiFieldType === 'multiselect';

  useEffect(() => {
    if (!error) {
      return;
    }

    if (isSelect) {
      selectRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [error, isSelect]);

  if (isSelect) {
    return (
      <Select
        key={uiField.key}
        label={uiField.name}
        required={isRequired}
        error={error}
        ref={selectRef}
        value={uiField.value}
        onChange={({ target }) => {
          uiField.value = target.value;

          setUIField({ ...uiField });
        }}
        options={[
          {
            key: 'choose',
            label: 'Please choose',
            value: ''
          },
          ...(uiField.allowedValues?.map(v => ({
            key: v.id,
            label: v.name,
            value: v.id
          })) ?? [])
        ]}
      />
    );
  }

  return (
    <TextField
      className="set-values-field"
      key={uiField.key}
      // data-field-name={integrationFieldName}
      // data-testid={type}
      required={isRequired}
      type={uiField.uiFieldType}
      error={error}
      fieldRef={inputRef}
      label={uiField.name}
      value={uiField.value}
      onChange={newValue => {
        uiField.value = newValue;

        setUIField({ ...uiField });
      }}
    />
  );
};
